import { useEffect, useRef, useState } from "react";
import { Sidebar, SIDEBAR_NAV_IDS } from "@/components/layout/Sidebar";
import { Header } from "@/components/layout/Header";
import { MobileNav } from "@/components/layout/MobileNav";
import { FilterBar } from "@/components/filters/FilterBar";
import { DrilldownProvider } from "@/components/drilldown/Drilldown";
import { OverviewKpis } from "@/components/OverviewKpis";
import { InsightsPanel } from "@/components/InsightsPanel";
import { CostProductieSection } from "@/components/sections/CostProductieSection";
import { MediaMobilaSection } from "@/components/sections/MediaMobilaSection";
import { ProfitabilitateSection } from "@/components/sections/ProfitabilitateSection";
import { IncarcareMasiniSection } from "@/components/sections/IncarcareMasiniSection";
import { ExportSplitSection } from "@/components/sections/ExportSplitSection";
import { TopClientiSection } from "@/components/sections/TopClientiSection";
import { MixSezonalitateSection } from "@/components/sections/MixSezonalitateSection";
import { PierderiSection } from "@/components/sections/PierderiSection";
import { LivrariSection } from "@/components/sections/LivrariSection";
import { ImportPanel } from "@/components/import/ImportPanel";
import { useDataVersion } from "@/data/store";

export default function App() {
  const [activeId, setActiveId] = useState(SIDEBAR_NAV_IDS[0]);
  const [importOpen, setImportOpen] = useState(false);
  const dataVersion = useDataVersion(); // re-desenează dashboard-ul la schimbarea sursei
  const mainRef = useRef<HTMLDivElement>(null);

  // Scroll-spy simplu: marchează în sidebar secțiunea vizibilă curent.
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        const vizibil = entries.find((e) => e.isIntersecting);
        if (vizibil?.target.id) setActiveId(vizibil.target.id);
      },
      { rootMargin: "-140px 0px -70% 0px", threshold: 0 },
    );
    SIDEBAR_NAV_IDS.forEach((id) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, [dataVersion]);

  function onNavigate(id: string) {
    setActiveId(id);
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  }

  return (
    <DrilldownProvider>
      <div className="min-h-screen overflow-x-hidden bg-slate-50 print:bg-white">
        <Sidebar activeId={activeId} onNavigate={onNavigate} />
        <div className="min-w-0 lg:pl-64 print:pl-0">
          <Header onOpenImport={() => setImportOpen(true)} />
          <div className="print:hidden">
            <MobileNav activeId={activeId} onNavigate={onNavigate} />
            <FilterBar />
          </div>
          <main
            ref={mainRef}
            className="mx-auto min-w-0 max-w-[1440px] space-y-8 px-4 py-6 sm:px-6 lg:px-8 lg:py-8 lg:space-y-12"
          >
            {/* key={dataVersion}: la un import / revenire la demo, secțiunile se remontează
                și re-citesc sursa curentă. Filtrele (state separat) NU remontează. */}
            <div key={dataVersion} className="space-y-8 lg:space-y-12">
              <OverviewKpis />
              <InsightsPanel />
              <CostProductieSection />
              <MediaMobilaSection />
              <ProfitabilitateSection />
              <IncarcareMasiniSection />
              <ExportSplitSection />
              <TopClientiSection />
              <MixSezonalitateSection />
              <PierderiSection />
              <LivrariSection />
            </div>
            <footer className="border-t border-slate-200 pb-10 pt-6 text-center text-xs text-slate-400">
              Glass Expert BI (P5) · date {"->"} @dragos · dashboard {"->"} @admin-architect · AXA IT
            </footer>
          </main>
        </div>

        <ImportPanel open={importOpen} onClose={() => setImportOpen(false)} />
      </div>
    </DrilldownProvider>
  );
}
